import { useMemo, useState } from "react";
import styled from "styled-components";
import { PopupModal } from "../../../components/TopBar/PopupModal/PopupModal";
import { ErrorInField } from "../../../../../components/atoms/Input";
import downArrow from "../../../../../assets/icons/downArrow.svg";

/**
 * SelectDropDown Component
 *
 * A customizable dropdown component that supports dynamic options, with optional labels,
 * required markers, support for selecting objects with IDs, and a styled modal dropdown.
 *
 * @component
 *
 * @param {string|number} widthOfSelectElement - Width of the dropdown element (e.g. "200px").
 * @param {string|number} heightOfSelectElement - Height of the dropdown element (optional).
 * @param {string} placeholderForInput - Placeholder text when no option is selected.
 * @param {boolean} requiredOption - If true, adds a '*' mark next to the label.
 * @param {Array} options - List of options to render. Can be strings or objects with `id` and `name`.
 * @param {Function} handleSelect - Callback triggered on selecting an option.
 * Receives (selectedValue, nameOfInput) as arguments.
 * @param {string} labelForInput - Label displayed above the dropdown.
 * @param {string} optionValOne - An optional first static option (like "Select All").
 * @param {string} nameOfInput - Name passed back to `handleSelect` for identifying the field.
 * @param {string|number} selectedVal - Currently selected value to display in the input area.
 * @param {boolean} isOptionWithId - If true, expects options as objects with `{id, name}`.
 * @param {string} errorMessage - An error message that show error info.
 *
 */

export const SelectDropDown = ({
  widthOfSelectElement,
  heightOfSelectElement,
  placeholderForInput,
  requiredOption,
  options,
  handleSelect,
  labelForInput,
  optionValOne,
  nameOfInput,
  selectedVal,
  isOptionWithId,
  errorMessage,
  setFieldTouched = () => {},
}) => {
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);

  const closeDropDown = () => {
    setIsDropDownOpen(false);
    setFieldTouched(nameOfInput, true);
  };

  const handleOptionSelect = (item, nameOfInput, data) => {
    handleSelect(item, nameOfInput, data);
    closeDropDown();
  };
  return (
    <SelectDropDownCss
      className="selectDropDown"
      width={widthOfSelectElement}
      height={heightOfSelectElement}
    >
      <div className="selectElement">
        {labelForInput && (
          <div className="labelForInput">
            {labelForInput} {requiredOption && <span>*</span>}
          </div>
        )}
        <div
          className={`inputValuePlace ${isDropDownOpen ? "focusInput" : ""} ${
            selectedVal ? "hasValue" : ""
          }`}
          onClick={() => setIsDropDownOpen(true)}
        >
          {selectedVal ? selectedVal : placeholderForInput}
        </div>

        {errorMessage && <ErrorInField message={errorMessage} />}
      </div>

      {isDropDownOpen && (
        <PopupModal closeModal={closeDropDown}>
          <div className="optionsOfSelect">
            {optionValOne && <div className="optionItem optionValOne">{optionValOne}</div>}
            {isOptionWithId
              ? options?.length > 0 &&
                options.map(item => (
                  <div
                    key={item.id}
                    className="optionItem"
                    onClick={() => handleOptionSelect(item.id, nameOfInput, item)}
                  >
                    {item.name}
                  </div>
                ))
              : options?.length > 0 &&
                options.map(item => (
                  <div
                    key={item}
                    className="optionItem"
                    onClick={() => handleOptionSelect(item, nameOfInput)}
                  >
                    {item}
                  </div>
                ))}
          </div>
        </PopupModal>
      )}
    </SelectDropDownCss>
  );
};

/**
 * SelectDropDownWrapperForQuery Component
 *
 * A wrapper around the SelectDropDown component that auto-fetches data using a query hook,
 * finds the selected item by ID, and passes the appropriate value to the dropdown.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {Function} props.useGetData - A custom hook (e.g., RTK Query hook) that returns `{ data }`
 * @param {number|string|null} props.selectedId - The ID of the selected option to preselect in dropdown
 * @param {...any} props - Any additional props passed to the underlying SelectDropDown component
 *
 * @returns {JSX.Element} A SelectDropDown component with fetched options and selected value
 *
 */

export const SelectDropDownWrapperForQuery = ({
  useGetData = () => ({ data: [] }),
  selectedId,
  ...props
}) => {
  const { data = [] } = useGetData();
  const selectedValue = useMemo(
    () => data.find(opt => opt.id === selectedId)?.name ?? null,
    [data, selectedId],
  );

  return <SelectDropDown options={data} selectedVal={selectedValue} {...props} />;
};

export const SelectDropDownCss = styled.div`
  position: relative;

  > .selectElement {
    position: relative;
    width: ${props => props.width || "auto"};
    cursor: pointer;

    > .labelForInput {
      color: var(--GREY, #4a4a68);
      font-feature-settings:
        "liga" off,
        "clig" off;
      font-size: 12px;
      font-style: normal;
      font-weight: 700;
      line-height: 20px;
      > span {
        color: var(--DANGER, #cf324a);
      }
    }
    > .inputValuePlace {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: ${props => props.height || "32px"};
      padding: 8px 4px 8px 12px;
      border-radius: 4px;
      border: 1px solid var(--LIGHT-ASH, #b9b9c6);
      background-color: var(--CLOUD, #fcfdfe);
      overflow: hidden;
      color: var(--ASH, #676879);
      text-overflow: ellipsis;
      font-weight: 400;
      line-height: 22px;
      font-size: 14px;

      background-image: url(${downArrow});
      background-origin: content-box;
      background-position: right 7px center;
      background-repeat: no-repeat;
      height: 32px;
      padding-left: 12px;

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }

  .optionsOfSelect {
    width: 100%;
    position: absolute;
    z-index: 5;
    border-radius: 4px;
    border: 1px solid var(--PEARL, #ecf1f4);
    background: #fff;
    padding: 4px 0px;
    max-height: 25vh;
    overflow: auto;

    .optionItem {
      padding: 5px 12px;
      color: var(--character-title-85, rgba(0, 0, 0, 0.85));
      font-weight: 400;
      line-height: 22px;
      cursor: pointer;
      &:hover {
        color: var(--BLUE, #2060ea);
        background-color: var(--MIST, #f7f8f9);
      }
    }
  }
`;
